import styled from 'styled-components';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { DateDropdown } from '@dev/ee-components';
import { Box } from '@material-ui/core';
import { AutomationContactType } from '@dev/ee-api/ee-api-models';
import { useParams } from 'react-router-dom';
import { safelyDecodeBase64Url } from '@dev/ee-tools';

import dayjs from '@/services/tools/dayjs';
import { DEFAULT_OFFSET, DEFAULT_PER_PAGE_COUNT, LONG_DATE_FORMAT } from '@/services/constants';
import { calculateOffset } from '@/services/tools/handleUrlParams';
import { GetAutomationContactsStart, getAutomationStore } from '@/store/automations/actions';
import { DateDropdownOptions } from '@/services/dictionaries/DateRangeValues';
import { GetAutomationContactsParams } from '@/services/ElasticApi';
import { useDateFilterData } from '@/services/tools/date';

const TableFilters = styled.div`
    display: flex;
    flex-direction: row;
    padding: 0px 48px 34px;
    justify-content: space-between;
`;

const ContactsFilters: FC = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { contactsListPagination } = useSelector(getAutomationStore);

    const perPage = contactsListPagination?.perPage ?? DEFAULT_PER_PAGE_COUNT;
    const page = contactsListPagination?.page ?? 1;
    const from = contactsListPagination?.from;
    const to = contactsListPagination?.to;
    const datesDiff = !!to && dayjs(to).diff(dayjs(from), 'day');

    const offset = calculateOffset(page, perPage);

    const urlParams: { name: string; recipientsType: AutomationContactType } = useParams();
    const requestParams: GetAutomationContactsParams = {
        offset,
        limit: perPage,
        name: safelyDecodeBase64Url(urlParams.name),
        type: urlParams.recipientsType,
        from,
        to,
    };

    return (
        <>
            <Box marginTop="32px" />
            <TableFilters>
                <DateDropdown
                    options={useDateFilterData()}
                    defaultPlaceholder={t('all')}
                    type="input"
                    returnRawData
                    defaultValue={datesDiff}
                    calendarOnChange={(startDate?: string, endDate?: string) => {
                        dispatch(
                            GetAutomationContactsStart({
                                ...requestParams,
                                to: endDate,
                                from: startDate,
                                offset: DEFAULT_OFFSET,
                            })
                        );
                    }}
                    onItemClick={(ev, value?: Record<string, string | number>) => {
                        dispatch(
                            GetAutomationContactsStart({
                                ...requestParams,
                                to: dayjs().format(LONG_DATE_FORMAT),
                                from:
                                    value?.value === DateDropdownOptions.All
                                        ? null
                                        : dayjs(dayjs().format(LONG_DATE_FORMAT))
                                              .subtract(value?.value as number, 'day')
                                              .format(LONG_DATE_FORMAT),
                                offset: DEFAULT_OFFSET,
                            })
                        );
                    }}
                    buttonVariant="tertiary"
                />
            </TableFilters>
        </>
    );
};

export default ContactsFilters;
